import { mapMetrics } from '@/utils/troubleshootingTree'
import { METRIC_KEY } from '@/core/constants'
import dayjs from 'dayjs'

const TYPE = {
  SET_CAMPAIGNS: 'SET_CAMPAIGNS',
  SET_SELECTED_CAMPAIGNS: 'SET_SELECTED_CAMPAIGNS',
  SET_CURRENCY: 'SET_CURRENCY',
  SET_ACCOUNT_ANALYSIS: 'SET_ACCOUNT_ANALYSIS',
  SET_ERROR: 'SET_ERROR',
  SET_PERFORMANCE_CHARTS: 'SET_PERFORMANCE_CHARTS',
  SET_CHART_SELECTION: 'SET_CHART_SELECTION',
}

const state = {
  campaigns: [],
  currency: '',
  accountAnalysis: {},
  error: null,
  selectedCampaigns: [],
  performanceCharts: [],
}

const mutations = {
  SET_CAMPAIGNS(state, campaigns) {
    state.campaigns = campaigns
  },
  SET_SELECTED_CAMPAIGNS(state, campaigns) {
    state.selectedCampaigns = campaigns
  },
  SET_CURRENCY(state, currency) {
    state.currency = currency
  },
  SET_ACCOUNT_ANALYSIS(state, analysis) {
    state.accountAnalysis = analysis
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_PERFORMANCE_CHARTS(state, charts) {
    state.performanceCharts = charts
  },
  SET_CHART_SELECTION(state, { links, isSelected }) {
    state.performanceCharts = state.performanceCharts.map(chart => {
      if (links.includes(chart.link)) {
        return { ...chart, selected: isSelected }
      }
      return chart
    })
  },
}

const getters = {
  weeklyDateRange(state) {
    return state.accountAnalysis?.campaign_group_data_labels
  },
  metrics(state) {
    return state.accountAnalysis?.campaign_group_data?.metrics
  },
  anomalies(state) {
    return state.accountAnalysis?.output
  },
  startDate(state) {
    return state.accountAnalysis?.campaign_group_data?.from
  },
  endDate(state) {
    return state.accountAnalysis?.campaign_group_data?.to
  },
  sessionId(state) {
    return state.accountAnalysis?.session_id
  },
  hideConversionValueMetrics(state) {
    return !state.performanceCharts.some(chart => chart.link === METRIC_KEY.conv_value)
  },
}

const actions = {
  async getCampaigns({ commit }, siteId) {
    const end_date = dayjs().format('YYYY-MM-DD')
    const start_date = dayjs().subtract(30, 'day').format('YYYY-MM-DD')

    commit(TYPE.SET_ERROR, null)
    try {
      const response = await axios.get(`/search/site/${siteId}/troubleshooting-tree/campaigns`, {
        params: {
          start_date,
          end_date,
        },
      })
      commit(TYPE.SET_CAMPAIGNS, response.data)
    } catch (error) {
      commit(TYPE.SET_ERROR, error)
      throw Error('Failed to load campaigns')
    }
  },
  async analyzeAccount({ commit, state, dispatch }, siteId) {
    commit(TYPE.SET_ERROR, null)
    try {
      const response = await axios.post(`/search/site/${siteId}/troubleshooting-tree/analyze-campaigns`, {
        campaign_ids: state.selectedCampaigns.map(campaign => campaign.campaign_id),
      })
      commit(TYPE.SET_ACCOUNT_ANALYSIS, response.data)
      dispatch('updatePerformanceCharts')
    } catch (error) {
      commit(TYPE.SET_ERROR, error)
      throw Error('Failed to analyze account')
    }
  },
  async fetchSiteDetails({ commit }, site_id) {
    try {
      const { data: siteDetails } = await axios.get(`/common/site/${site_id}`)
      const currency = siteDetails.currency
      commit(TYPE.SET_CURRENCY, currency)
      return siteDetails
    } catch (error) {
      throw Error('Failed to load site details')
    }
  },
  async sendChatMessage({ commit }, { siteId, sessionId, content }) {
    commit('SET_ERROR', null)
    try {
      const response = await axios.post(
        `/search/site/${siteId}/troubleshooting-tree/chat`,
        { content },
        {
          params: { session_id: sessionId },
          headers: { 'Content-Type': 'application/json' },
        },
      )
      return response.data.message
    } catch (error) {
      console.error('Error sending chat message', error)
      throw error
    }
  },
  updateSelectedCampaigns({ commit }, campaigns) {
    commit(TYPE.SET_SELECTED_CAMPAIGNS, campaigns)
  },
  updatePerformanceCharts({ commit, getters }) {
    const charts = mapMetrics(getters.metrics, getters.anomalies, getters.weeklyDateRange)
    commit(TYPE.SET_PERFORMANCE_CHARTS, charts)
  },
  highlightCharts({ commit }, { links, isSelected }) {
    commit(TYPE.SET_CHART_SELECTION, { links, isSelected })
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
